import { DataTable, DateFormat, Icon, Number } from "components/common"
import { Row, Space } from "antd"
import { useEffect, useMemo } from "react"

import { TitleDescription } from "components/custom"
import { getAreaUnit, joinBytItems, localisedValue, convertToSpecificAreaUnit } from "utils"
import { useLazyGetListingLogsQuery } from "features/inventory/api"
import { t } from "i18next"
import CollapsibleText from "components/common/collapsibleText"
import { useSelector } from "react-redux"

const getColumnData = data => {
  return data?.map(e => {
    return (
      <>
        <Row gutter={[10, 10]}>
          <Space size={2}>
            <div className="gray-700">{e[0]}</div> <div>{e[1] || "-"}</div>
          </Space>
        </Row>
      </>
    )
  })
}

const columns = userPlatformKey => [
  { title: t("Logged"), dataIndex: "logged", key: "logged" },
  { title: t("Unit Detail"), dataIndex: "unitDetail", key: "unitDetail" },
  { title: t("Location"), dataIndex: "location", key: "location" },
  { title: t("Price & Discount"), dataIndex: "price", key: "price" },
  { title: `${t("Area")} (${getAreaUnit(userPlatformKey)})`, dataIndex: "area", key: "area" },
  { title: t("Status"), dataIndex: "status", key: "status" },
  { title: t("State"), dataIndex: "state", key: "state" },
  { title: t("Status Comment"), dataIndex: "comment", key: "comment" },
]

const UnitLogsTable = ({ inventoryId }) => {
  const [getUnitLogs, { data: unitLogs, isFetching: isFetchingUnitLogs, error: errorUnitLogs }] =
    useLazyGetListingLogsQuery()
  const userInfo = useSelector(state => state?.app?.user?.info)
  const userPlatformKey = userInfo?.AgencyConfiguration?.Platform?.platformKey
  useEffect(() => {
    getUnitLogs(inventoryId)
  }, [inventoryId])

  const unitLogsData = useMemo(() => {
    return unitLogs?.map((e, index) => {
      return {
        key: index,
        logged: (
          <TitleDescription
            title={localisedValue({ key: "name", object: e?.LogAddedBy }) || "-"}
            gapSpace={4}
            description={<DateFormat date={e.createdAt} dateClassName="" />}
          />
        ),
        unitDetail: (
          <TitleDescription
            title={localisedValue({ key: "name", object: e.Project }) || "-"}
            gapSpace={4}
            description={joinBytItems(e.unitInfo, <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />)}
          />
        ),
        price: getColumnData([
          [
            t("Price") + ":",
            (
              <Number
                tooltip={true}
                digitAfterFraction={2}
                isStringOnly
                value={e.price}
                currencyUnit={e?.Currency?.currencyCode}
              />
            ) || "-",
          ],
          [t("Discount") + "(%)", e.maxDiscount || "-"],
        ]),

        area: getColumnData([
          [
            t("Net") + ": ",
            (
              <Number
                digitAfterFraction={4}
                isStringOnly
                value={convertToSpecificAreaUnit(e?.landArea, userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f")}
              />
            ) || "-",
          ],
          [
            t("Gross") + ": ",
            (
              <Number
                digitAfterFraction={4}
                isStringOnly
                value={convertToSpecificAreaUnit(e?.grossArea, userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f")}
              />
            ) || "-",
          ],
        ]),
        status: (
          <TitleDescription
            title={localisedValue({ key: "name", object: e?.ListingStatus }) || "-"}
            gapSpace={4}
            description={
              e?.StatusUpdatedBy?.name &&
              `${t("By")}: ${localisedValue({ key: "name", object: e?.StatusUpdatedBy }) || "-"}`
            }
          />
        ),
        state: localisedValue({ key: "name", object: e.ListingAllocationType }) || "-",
        location: localisedValue({ key: "breadcrumbTitle", object: e?.ProjectSection }) || "-",
        comment:
          e?.ListingStatus?.name === "Hold" || e?.ListingStatus?.name === "Sold" ? (
            <CollapsibleText text={localisedValue({ object: e, key: "comments" })} collapsibleLength={150} />
          ) : (
            "-"
          ),
      }
    })
  }, [unitLogs])

  return (
    <div style={{ paddingTop: 12 }}>
      <DataTable
        columns={columns(userPlatformKey)}
        data={unitLogsData || []}
        noUrlPush={true}
        stickyOffset={-25}
        loading={isFetchingUnitLogs}
        error={errorUnitLogs}
        scroll={{
          x: 1700,
        }}
      />
    </div>
  )
}

export default UnitLogsTable
